import React, { useState, useEffect } from "react";
import api from "../api";
import {
  Card, CardContent, Typography, CircularProgress, Box
} from "@mui/material";
import {
  Chart as ChartJS, LineElement, CategoryScale, LinearScale,
  PointElement, Title, Tooltip, Legend, Filler
} from "chart.js";
import { Line } from "react-chartjs-2";

// Register Chart.js components
ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Normalize UI period to backend values
const normalizePeriod = (period) => {
  switch (period.toLowerCase()) {
    case "today":
      return "today";
    case "weekly":
      return "week";
    case "monthly":
      return "month";
    default:
      return "today";
  }
};

function ChartPanel({ period }) {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/Dashboard/chartdata/${normalizePeriod(period)}`)
      .then((res) => {
        setChartData(res.data);
        setError(null);
      })
      .catch((err) => {
        console.error("Error fetching chart data:", err);
        setError("Failed to load chart data");
      })
      .finally(() => setLoading(false));
  }, [period]);

  const data = {
    labels: chartData.map((d) =>
      d.date ? new Date(d.date).toLocaleDateString("en-IN") : ""
    ),
    datasets: [
      {
        label: "Revenue (₹)",
        data: chartData.map((d) => d.revenue ?? 0),
        borderColor: "#2563EB",
        backgroundColor: "rgba(37,99,235,0.2)",
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6
      },
      {
        label: "Orders",
        data: chartData.map((d) => d.orders ?? 0),
        borderColor: "#16A34A",
        backgroundColor: "rgba(22,163,74,0.2)",
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false, // 👈 allows chart to resize dynamically
    interaction: {
      mode: "nearest", // 👈 tooltips follow finger/mouse
      axis: "x",
      intersect: false
    },
    plugins: {
      title: {
        display: true,
        text: `Sales Trend (${period})`,
        font: { size: 16, weight: "bold" },
        color: "#374151"
      },
      legend: {
        position: "top",
        labels: {
          boxWidth: 12,
          font: { size: 12 }
        }
      },
      tooltip: {
        enabled: true,
        mode: "index",
        intersect: false,
        bodyFont: { size: 12 },
        titleFont: { size: 13 },
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.formattedValue}`
        }
      }
    },
    scales: {
      x: {
        title: { display: true, text: "Date", font: { size: 12 } },
        ticks: { maxRotation: 45, minRotation: 0, font: { size: 11 } },
        grid: { color: "#f3f4f6" }
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Value", font: { size: 12 } },
        ticks: { font: { size: 11 } },
        grid: { color: "#f3f4f6" }
      }
    }
  };

  return (
    <Card sx={{ margin: 2 }}>
      <CardContent>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontWeight: "bold",
            color: "#2563EB",
            display: "flex",
            alignItems: "center",
            gap: 1
          }}
        >
          📊 Sales Chart
        </Typography>

        {loading && <CircularProgress size={24} />}
        {error && <Typography color="error">{error}</Typography>}

        {!loading && !error && chartData.length > 0 && (
          <Box sx={{ height: { xs: 250, sm: 350, md: 400 } }}> 
            {/* 👆 responsive height: smaller on mobile */}
            <Line data={data} options={options} />
          </Box>
        )}

        {!loading && !error && chartData.length === 0 && (
          <Typography>No chart data found</Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default ChartPanel;